import AppsIcon from '@mui/icons-material/Apps';
import { Box, ClickAwayListener, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router';

import { navigationConstants } from '@/shared/constants';

import { handlePortalHomeNavigation } from '../utils';

interface AppHubMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AppHubMenu = ({ open, onOpenChange }: AppHubMenuProps) => {
  const navigate = useNavigate();

  return (
    <ClickAwayListener
      onClickAway={() => {
        onOpenChange(false);
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <Tooltip title="App hub" arrow placement="bottom">
          <IconButton
            aria-label="Open app hub"
            onClick={() => {
              onOpenChange(!open);
            }}
            color="inherit"
            sx={{ color: '#fff' }}
          >
            <AppsIcon />
          </IconButton>
        </Tooltip>

        {open && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: 'calc(100% + 10px)',
              right: 0,
              width: { xs: 'min(320px, calc(100vw - 16px))', sm: 320 },
              maxWidth: 'calc(100vw - 16px)',
              borderRadius: 3,
              p: { xs: 1.5, sm: 2.5 },
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.common.white,
              color: theme.palette.text.primary,
              boxShadow: theme.shadows[12],
              border: `1px solid ${theme.palette.divider}`,
              zIndex: theme.zIndex.modal,
            })}
          >
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="#"
                underline="none"
                color="inherit"
                onClick={(event) => {
                  event.preventDefault();
                  handlePortalHomeNavigation({
                    navigate,
                    onNavigate: () => {
                      onOpenChange(false);
                    },
                  });
                }}
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {navigationConstants.homeTitle}
              </Link>
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(80px, 1fr))',
                },
                gap: 1.5,
              }}
            >
              {navigationConstants.apps.map((app) => {
                const Icon = app.icon;

                return (
                  <Link
                    key={app.id}
                    component={RouterLink}
                    to={app.path}
                    underline="none"
                    color="inherit"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                      p: 1,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Icon sx={{ fontSize: 40 }} />
                    <Typography variant="caption" sx={{ textAlign: 'center' }}>
                      {app.label}
                    </Typography>
                  </Link>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default AppHubMenu;
